import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { useI18n } from "../lib/i18n";
import { useAuth, API } from "../lib/auth";
import StatusBadge from "../components/StatusBadge";
import JobCard from "../components/JobCard";
import CandidateProfile from "./CandidateProfile";
import CandidatePreferences from "../components/CandidatePreferences";
import { Briefcase, FileText, Newspaper, Sliders, User } from "lucide-react";

export default function CandidateDashboard() {
  const { t } = useI18n();
  const { user, authHeader } = useAuth();
  const [apps, setApps] = useState([]);
  const [latest, setLatest] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/candidate/applications`, { headers: authHeader }).then(r => setApps(r.data || []));
    axios.get(`${API}/jobs?limit=6`).then(r => setLatest(r.data.jobs || []));
    axios.get(`${API}/news?limit=5`).then(r => setNews(r.data || []));
    // eslint-disable-next-line
  }, []);

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]" data-testid="candidate-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">{t("candidate.dashboard")}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Bonjour, {user?.name?.split(" ")[0] || "Candidat"}</h1>
        </div>

        <Tabs defaultValue="apps">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
            <TabsTrigger value="apps" data-testid="tab-apps"><FileText className="h-4 w-4 mr-2" />{t("candidate.myApps")}</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile"><User className="h-4 w-4 mr-2" />{t("candidate.profile")}</TabsTrigger>
            <TabsTrigger value="prefs" data-testid="tab-prefs"><Sliders className="h-4 w-4 mr-2" />Preferences</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-latest"><Briefcase className="h-4 w-4 mr-2" />{t("candidate.latestJobs")}</TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news"><Newspaper className="h-4 w-4 mr-2" />{t("candidate.news")}</TabsTrigger>
          </TabsList>

          <TabsContent value="apps" className="mt-6">
            <Card className="overflow-hidden">
              {apps.length === 0 ? (
                <div className="text-center py-16 text-slate-500" data-testid="apps-empty">{t("candidate.noApps")}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-700 uppercase text-xs tracking-wider">
                      <tr>
                        <th className="px-6 py-3 text-left">{t("candidate.job")}</th>
                        <th className="px-6 py-3 text-left">{t("candidate.company")}</th>
                        <th className="px-6 py-3 text-left">{t("candidate.date")}</th>
                        <th className="px-6 py-3 text-left">{t("candidate.status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apps.map((a) => (
                        <tr key={a.id} data-testid={`my-app-row-${a.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-6 py-4 font-medium text-slate-900">{a.job_title}</td>
                          <td className="px-6 py-4 text-slate-600">{a.company_name || "—"}</td>
                          <td className="px-6 py-4 text-slate-600">{new Date(a.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4"><StatusBadge status={a.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {latest.map(j => <JobCard key={j.id} job={j} />)}
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <CandidateProfile />
          </TabsContent>

          <TabsContent value="prefs" className="mt-6">
            <CandidatePreferences />
          </TabsContent>

          <TabsContent value="news" className="mt-6">
            <div className="space-y-4">
              {news.map(n => (
                <Card key={n.id} className="p-6" data-testid={`news-${n.id}`}>
                  <p className="text-xs uppercase tracking-widest text-[#1A8FE3] font-semibold">{new Date(n.created_at).toLocaleDateString()}</p>
                  <h3 className="font-display text-xl font-semibold text-slate-900 mt-1">{n.title}</h3>
                  <p className="text-slate-600 mt-2 leading-relaxed">{n.content}</p>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
